import type { AttachmentSpec } from "./types";
import { DURATIONS } from "./constants";
import { modelRegistry } from "./registry";
import {
  FeatureOptionTypeMap,
  ModelSpecNode,
  OperationParamsSchema,
  ProviderBinding,
  ProviderName,
  STEP_FEATURES,
  StepFeatureKey,
  StepFeatureMap,
  StepOptions,
  StepType,
} from "./types";

export function withDefaultStepFeatures(
  features?: StepFeatureMap,
): Record<StepFeatureKey, boolean> {
  return Object.fromEntries(
    STEP_FEATURES.map((key) => [key, Boolean(features?.[key])]),
  ) as Record<StepFeatureKey, boolean>;
}

type MinimalSpecNode = {
  spec?: {
    options?: StepOptions;
    features?: StepFeatureMap;
  };
};

export function stepFeatures<
  R extends Record<string, unknown>,
  K extends keyof R & string,
>(model: K, registry: R) {
  const node = registry[model] as unknown as MinimalSpecNode | undefined;
  return withDefaultStepFeatures(node?.spec?.features);
}

export function stepOptions<
  R extends Record<string, unknown>,
  K extends keyof R & string,
>(model: K, registry: R): StepOptions | undefined {
  const node = registry[model] as unknown as MinimalSpecNode | undefined;
  return node?.spec?.options;
}

export function getOptionItems<
  R extends Record<string, unknown>,
  K extends keyof R & string,
>(model: K, featureKey: keyof FeatureOptionTypeMap, registry: R) {
  const options = stepOptions(model, registry);
  const option = options?.[featureKey];
  return option && "items" in (option as object)
    ? (
        option as {
          items: readonly { label: string; value: string }[];
        }
      ).items
    : undefined;
}

export function getOptionConfig<
  R extends Record<string, unknown>,
  K extends keyof R & string,
  F extends keyof FeatureOptionTypeMap,
>(model: K, featureKey: F, registry: R): FeatureOptionTypeMap[F] | undefined {
  const options = stepOptions(model, registry);
  return options?.[featureKey] as FeatureOptionTypeMap[F] | undefined;
}

export function getProviderBinding<
  R extends Record<string, unknown>,
  K extends keyof R & string,
  TNode extends R[K] = R[K],
  TParams extends OperationParamsSchema = TNode extends ModelSpecNode<
    infer P,
    StepFeatureMap,
    StepType
  >
    ? P
    : OperationParamsSchema,
  TStep extends StepType = TNode extends ModelSpecNode<
    OperationParamsSchema,
    StepFeatureMap,
    infer S
  >
    ? S
    : StepType,
>(
  model: K,
  provider: ProviderName,
  registry: R,
): ProviderBinding<TStep, TParams> | undefined {
  const node = registry[model] as unknown as ModelSpecNode<
    TParams,
    StepFeatureMap,
    TStep
  >;
  return node.providers.find(
    (providerBinding): providerBinding is ProviderBinding<TStep, TParams> =>
      providerBinding.provider === provider,
  );
}

export function isModelKey(key: unknown): key is ModelKey {
  return typeof key === "string" && key in modelRegistry;
}

export type ModelKey = Extract<keyof typeof modelRegistry, string>;

export function durationItems(model: string) {
  if (!isModelKey(model)) return DURATIONS;
  const result = getOptionItems(model, "duration", modelRegistry);

  if (!result) return DURATIONS;
  return result;
}

export function calculateMegapixelsImageCost(
  width: number,
  height: number,
  costPerMegapixel: number,
) {
  // 1 megapixel = 1,000,000 pixels
  const totalPixels = width * height;
  const megapixels = totalPixels / 1_000_000;

  // Calculate total cost
  const cost = megapixels * costPerMegapixel;

  return cost;
}

function listAttachmentSpecs(
  modelKey: string | undefined,
): readonly AttachmentSpec[] {
  if (!modelKey) return [];
  const node = modelRegistry[modelKey as keyof typeof modelRegistry];
  const features = node?.spec.features;
  const hasAttachments = Boolean(
    features && "attachments" in features && features.attachments,
  );
  if (!hasAttachments) return [];

  const raw = (
    node?.spec.options as
      | { attachments?: readonly AttachmentSpec[] }
      | undefined
  )?.attachments;

  if (!raw || !Array.isArray(raw) || raw.length === 0) return [];
  // Somehow we need to cast this to the correct type
  return raw as readonly AttachmentSpec[];
}

export function getAttachmentSlotLabel(
  modelKey: string | undefined,
  slot: string,
): string | undefined {
  const specs = listAttachmentSpecs(modelKey);
  return specs.find((spec) => spec.slot === slot)?.label;
}

export function getAttachmentGroupLabel(
  modelKey: string | undefined,
  groupKey: string,
): string | undefined {
  const specs = listAttachmentSpecs(modelKey);
  if (specs.length === 0) return undefined;

  const labelBySlot = new Map(specs.map((spec) => [spec.slot, spec.label]));
  const groupSlots = specs
    .filter((spec) => (spec.group ?? spec.slot) === groupKey)
    .map((spec) => spec.slot);

  if (groupSlots.length === 0) return undefined;

  const labels = groupSlots
    .map((slot) => labelBySlot.get(slot))
    .filter((label): label is string => typeof label === "string");

  if (labels.length === 0) return undefined;
  return labels.length === 1 ? labels[0] : labels.join(" / ");
}

type EstimateStepCostParams = {
  model: string;
  type: StepType;
  params: Record<string, unknown>;
  prompt?: string;
};

type VideoCostBinding = {
  estimateCost: (
    params: Record<string, unknown> & { prompt: string },
  ) => number;
  estimateCostPerSecond?: (
    params: Record<string, unknown> & { prompt: string },
  ) => number;
  costPerSecond?: number;
  pricingUnit?: "per_second" | "per_generation";
};

/**
 * Estimates the cost for a single inference step.
 * For videos, returns cost per second in microdollars.
 * For images, returns cost per image in microdollars.
 * Returns 0 if model/binding not found or params insufficient.
 */
export function estimateStepCost({
  model,
  type,
  params,
  prompt = "",
}: EstimateStepCostParams): number {
  if (!isModelKey(model)) {
    return 0;
  }

  // Get the model node from registry
  const node = modelRegistry[model];
  if (node?.stepType !== type) {
    return 0;
  }

  // Get default provider for the model (prefer "fal", fallback to first)
  const preferredProvider =
    node.providers.find((p) => p.provider === "fal")?.provider ??
    node.providers[0]?.provider;
  if (!preferredProvider) {
    return 0;
  }
  const provider = preferredProvider;

  // Get provider binding
  const binding = getProviderBinding<typeof modelRegistry, ModelKey>(
    model,
    provider,
    modelRegistry,
  );

  if (!binding?.estimateCost) {
    return 0;
  }

  // Prepare full params including prompt
  const fullParams: Record<string, unknown> & { prompt: string } = {
    ...params,
    prompt,
  };

  try {
    // For videos, prefer a parameter-aware unit cost when available.
    if (type === "video") {
      const videoBinding = binding as VideoCostBinding;

      // Flat-rate models (e.g. video-edit): return total cost, not per-second
      if (videoBinding.pricingUnit === "per_generation") {
        return Math.max(0, Number(binding.estimateCost(fullParams)));
      }

      if (typeof videoBinding.estimateCostPerSecond === "function") {
        return Math.max(0, videoBinding.estimateCostPerSecond(fullParams));
      }

      if (typeof videoBinding.costPerSecond === "number") {
        return Math.max(0, videoBinding.costPerSecond);
      }

      // Fallback: calculate from estimateCost if costPerSecond not available
      const totalCostMicrodollars = Number(binding.estimateCost(fullParams));
      const costMicrodollars = Math.max(0, totalCostMicrodollars);

      // Get duration from params (could be 'duration' or 'totalSeconds')
      const duration =
        (params.duration as string | undefined) ??
        (params.totalSeconds as string | undefined);

      if (duration) {
        const durationSeconds = Number(duration);
        if (durationSeconds > 0 && Number.isFinite(durationSeconds)) {
          // Return cost per second
          return costMicrodollars / durationSeconds;
        }
      }
      // If no duration found, return 0 (can't calculate per-second cost)
      return 0;
    }

    // For images and other types, call estimateCost and return cost per item
    const totalCostMicrodollars = Number(binding.estimateCost(fullParams));
    return Math.max(0, totalCostMicrodollars);
  } catch {
    return 0;
  }
}

/** Check if a video model uses flat per-generation pricing (not per-second). */
export function checkIsPerGenerationModel(model: string): boolean {
  if (!isModelKey(model)) return false;
  const node = modelRegistry[model];
  // Use same fal-preferred provider selection as estimateStepCost
  const binding = (node?.providers.find((p) => p.provider === "fal") ??
    node?.providers[0]) as VideoCostBinding | undefined;
  return binding?.pricingUnit === "per_generation";
}
